/**
 * Loading spinner for the cart drawer.
 *
 * Shown while a line is being added, removed or re-quantified, and cleared once
 * the cart has come back and the drawer has re-rendered.
 *
 * Busy is detected two ways, because the two paths signal differently:
 *   1. The theme's own cart mutations add `cart-items-disabled` to the cart items
 *      component for the duration of the request (see component-cart-items.js).
 *      A MutationObserver on class changes picks that up without patching theme JS.
 *   2. Quick Add fetches /cart/add.js directly and never touches that class, so
 *      its clicks are tracked here and released on `cart:update`.
 *
 * A hard timeout backstops both. A spinner that gets stuck on is worse than one
 * that never appears, so it always clears itself.
 *
 * v2 fixes a real bug: the Quick Add path released on a flat 60ms delay after
 * the FIRST cart:update. But that is not necessarily the last one — Quick Add's
 * own announce() can be followed by a SECOND cart:update from
 * mimu-cart-extras.js, which does its own /cart.js fetch and, when it finds and
 * removes an orphaned gift line, fetches again and re-announces. The flat delay
 * released the spinner mid-cascade, so the cart kept visibly changing after the
 * ring had already disappeared. Now it is a real debounce: every cart:update
 * resets the release timer, so it only fires after a genuine quiet period with
 * no further updates — however many rounds actually happen.
 */
(function () {
  if (window.__mimuCartSpinner) return;
  window.__mimuCartSpinner = true;

  var QUICK_ADD_TIMEOUT = 8000;
  // Quiet period required, with no further cart:update, before releasing.
  var SETTLE_DELAY = 500;
  var quickAddBusy = false;
  var quickAddTimer = null;
  var settleTimer = null;
  var node = null;

  function host() {
    return document.querySelector('.cart-drawer__dialog');
  }

  function ensure() {
    var h = host();
    if (!h) return null;
    if (!node || !node.isConnected) {
      node = h.querySelector('[data-mimu-spinner]');
      if (!node) {
        node = document.createElement('div');
        node.className = 'mimu-spin';
        node.setAttribute('data-mimu-spinner', '');
        // Announced rather than hidden: a screen reader should know the cart is
        // working, but the ring itself is decorative.
        node.setAttribute('role', 'status');
        node.setAttribute('aria-live', 'polite');
        node.innerHTML =
          '<span class="mimu-spin__ring" aria-hidden="true"></span>' +
          '<span class="visually-hidden">Updating your cart</span>';
        h.appendChild(node);
      }
    }
    return node;
  }

  function themeBusy() {
    return !!document.querySelector('.cart-items-disabled');
  }

  function sync() {
    var el = ensure();
    if (!el) return;
    el.classList.toggle('is-on', themeBusy() || quickAddBusy);
  }

  function releaseQuickAdd() {
    quickAddBusy = false;
    clearTimeout(quickAddTimer);
    clearTimeout(settleTimer);
    sync();
  }

  document.addEventListener(
    'click',
    function (event) {
      var btn = event.target.closest ? event.target.closest('[data-mimu-qa-add]') : null;
      if (!btn) return;
      quickAddBusy = true;
      clearTimeout(quickAddTimer);
      clearTimeout(settleTimer);
      quickAddTimer = setTimeout(releaseQuickAdd, QUICK_ADD_TIMEOUT);
      sync();
    },
    true
  );

  // The drawer re-renders on cart:update, but that is not necessarily the LAST
  // one — see the v2 note above. Debounce: every event pushes the release back
  // out, so it only fires once updates have genuinely stopped.
  document.addEventListener('cart:update', function () {
    clearTimeout(settleTimer);
    settleTimer = setTimeout(releaseQuickAdd, SETTLE_DELAY);
  });

  new MutationObserver(sync).observe(document.documentElement, {
    subtree: true,
    childList: true,
    attributes: true,
    attributeFilter: ['class']
  });

  sync();
})();
